//@author: devin

import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { AngularJSComponentUtil } from "../../util/angularjs.component.util";
import { S25Util } from "../../util/s25-util";
import { PendingObjectService } from "../../services/pending.object.service";
import { EventService } from "../../services/event.service";

/**
 * Wrapper component for the AngularJS s25EventCreationForm.
 * This allows the AngularJS event creation form to be used within modern Angular components.
 */
@Component({
    selector: "s25-ng-event-creation-form-wrapper",
    template: `
        <div class="event-creation-form-wrapper">
            <div #formContainer class="form-container"></div>
        </div>
    `,
    styles: `
        .event-creation-form-wrapper {
            width: 100%;
            height: 100%;
        }

        .form-container {
            width: 100%;
            min-height: 400px;
        }
    `,
    standalone: false,
})
export class S25NgEventCreationFormWrapperComponent implements OnInit, OnDestroy {
    @ViewChild("formContainer", { static: true }) formContainer!: ElementRef;

    @Input() organizationId?: number;
    @Input() copyEventId?: string;
    @Input() mode: string = "create";
    @Input() onFormClose?: () => void;
    @Input() onFormSave?: (eventId: number) => void;

    private angularJSComponentUtil = new AngularJSComponentUtil();
    private componentInstance: any = null;

    constructor() {}

    ngOnInit() {
        void this.initializeForm();
    }

    ngOnDestroy() {
        if (this.componentInstance) {
            this.componentInstance.destroy();
        }
        this.angularJSComponentUtil.destroyAllComponents();
    }

    /**
     * Initializes the AngularJS event creation form.
     */
    private async initializeForm() {
        try {
            if (!window.angBridge) {
                throw new Error("angBridge is not available");
            }

            const { $q } = window.angBridge;

            // Prepare the model with callback for organization
            // The AngularJS component expects model.modelCallback to be a function
            const model = {
                modelCallback: (modelInstance: any) => {
                    const promises: any[] = [];

                    // Add primary organization if provided
                    if (this.organizationId) {
                        promises.push(modelInstance.addPrimaryOrganization(this.organizationId));
                    }

                    return $q.all(promises);
                },
            };

            const { eventId, profileId } = await EventService.getNewEventProfileId();
            void EventService.primeEventPut(eventId);

            // Prepare bindings for the AngularJS component
            const bindings = {
                copyEventId: this.copyEventId || "",
                eventId: eventId,
                newProfileId: profileId,
                mode: this.mode,
                model: model,
                apiBean: {
                    broadcastToParentWindow: (data: any) => {
                        // Handle form events
                        if (data.eventSaved && data.events?.event?.[0]?.event_id) {
                            const eventId = parseInt(data.events.event[0].event_id);
                            this.onFormSave?.(eventId);
                        }
                    },
                },
                pending: {
                    4: 0,
                    6: 0,
                },
            };

            try {
                // Create the AngularJS component
                this.componentInstance = await this.angularJSComponentUtil.createAngularJSComponentWithDefer(
                    "s25-event-creation-form",
                    this.formContainer,
                    bindings,
                    "defer",
                );

                // Override the close method after component is initialized
                setTimeout(() => {
                    if (this.componentInstance && this.componentInstance.scope) {
                        // Try to find the component controller
                        const scope = this.componentInstance.scope;
                        let controller = null;

                        // Look for the controller in various places
                        if (scope.$$childHead && scope.$$childHead.self) {
                            controller = scope.$$childHead.self;
                        } else if (scope.self) {
                            controller = scope.self;
                        }

                        if (controller && (controller as any).close) {
                            (controller as any).close = (eventId: any, skipRedirects: any) => {
                                if (eventId) {
                                    void PendingObjectService.deleteAll(eventId, 4);
                                    void PendingObjectService.deleteAll(eventId, 6);
                                }
                                this.onFormClose?.();
                            };
                        } else {
                            throw new Error("Could not find controller to override close method");
                        }
                    }
                }, 100); // Small delay to ensure component is fully initialized
            } catch (error: any) {
                console.error("Event creation form initialization failed:", error);
                S25Util.showError("Failed to initialize event creation form");
            }
        } catch (error) {
            console.error("Failed to initialize event creation form:", error);
            S25Util.showError("Failed to initialize event creation form");
        }
    }
}
